import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, CardBody,
  Table, Button, Row, Col, Input, Badge, Spinner
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { dalaActions } from "../../sagas/dalaSlice";
import { FaPlus, FaEdit, FaSearch, FaLayerGroup, FaFilePdf, FaTimesCircle } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import "./DalaListPage.css";

export default function DalaListPage() {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dalas, loading } = useSelector(s => s.dala || {});
  const { company } = useSelector(s => s.auth || {});
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    dispatch(dalaActions.list?.({ query: debouncedSearch }));
  }, [debouncedSearch]);

  const filteredDalas = useMemo(() => {
    if (!debouncedSearch) return dalas || [];
    const q = debouncedSearch.toLowerCase();
    return (dalas || []).filter((d) => {
      const haystack = [
        d?.id,
        d?.numero,
        d?.date,
        d?.clientNom,
        d?.transporteurNom,
        d?.commercialNom,
        d?.createdBy,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [dalas, debouncedSearch]);

  const totalSurface = filteredDalas.reduce((a, b) => a + (b.totalSurface || 0), 0);
  const totalMontant = filteredDalas.reduce((a, b) => a + (b.total || 0), 0);
  const totalLignes = filteredDalas.reduce((a, b) => a + (b.details?.length || 0), 0);
  const avgMontant = filteredDalas.length ? totalMontant / filteredDalas.length : 0;

  const calculateRow = (r) => {
    const largeur = Number(r?.largeur || 0);
    const longeur = Number(r?.longeur || 0);

    if (largeur > 0 && longeur > 0) {
      const largeurML = largeur + 0.2;
      let nbP = Number((longeur / 0.6).toFixed(6));
      const decimalPart = Number((nbP % 1).toFixed(6));
      nbP = decimalPart < 0.83 ? Math.trunc(nbP) : Math.round(nbP);
      if (r?.depart) nbP += 1;

      const surface = largeur * longeur;
      const nbHourdis = surface * 8.4;
      const prixUnitaire = Number(r?.prixUnitaire || 0);
      const montantLigne = nbP * prixUnitaire;

      return { ...r, largeurML, nbP, surface, nbHourdis, montantLigne, prixUnitaire };
    }

    const prixUnitaire = Number(r?.prixUnitaire || 0);
    const quantite = Number(r?.quantite || 0);
    return { ...r, nbP: quantite, montantLigne: quantite * prixUnitaire, prixUnitaire };
  };

  const isPoutrelleArticle = (row) =>
    String(row?.articleNom || "").trim().toLowerCase().startsWith("poutrelle");

  const getTotalPoutrelle = (rows) => {
    const rowsWithDimensions = rows.filter(
      (r) => Number(r?.largeur) > 0 && Number(r?.longeur) > 0
    );
    const rowsFromArticles = rows.filter(
      (r) => !(Number(r?.largeur) > 0 && Number(r?.longeur) > 0) && isPoutrelleArticle(r)
    );

    const totalFromDimensions = rowsWithDimensions.reduce((a, b) => a + Number(b?.nbP || 0), 0);
    const totalFromArticles = rowsFromArticles.reduce((a, b) => a + Number(b?.quantite || 0), 0);

    return totalFromDimensions + totalFromArticles;
  };

  const previewPdf = (dala) => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;

    const details = dala?.details || [];
    const calculatedRows = details.map(calculateRow);
    const filteredRows = calculatedRows.filter(r => Number(r.largeur) > 0 && Number(r.longeur) > 0);

    const totalSurfaceCalc = filteredRows.reduce((a, b) => a + (b.surface || 0), 0);
    const totalMLCalc = filteredRows.reduce((a, b) => a + ((b.largeurML || 0) * (b.nbP || 0)), 0);
    const totalHourdisCalc = filteredRows.reduce((a, b) => a + (b.nbHourdis || 0), 0) + 5;
    const totalPoutrelleCalc = getTotalPoutrelle(calculatedRows);
    const totalMontantCalc = calculatedRows.reduce((a, b) => a + (b.montantLigne || 0), 0);
    const nombrePTSCalc = Math.ceil(totalSurfaceCalc / 10);
    const prixMetreCarreCalc = totalSurfaceCalc > 0 ? totalMontantCalc / totalSurfaceCalc : 0;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text((company?.name || "MA SOCIETE").toUpperCase(), pageWidth / 2, 14, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(company?.subtitle || "", pageWidth / 2, 20, { align: "center" });
    doc.line(margin, 24, pageWidth - margin, 24);
    const tableMargin = { left: margin, right: margin, bottom: 22 };

    autoTable(doc, {
      startY: 28,
      head: [["Champ", "Valeur"]],
      body: [
        ["Reference", `#${dala?.id || "-"}`],
        ["Date", dala?.date || "-"],
        ["Client", dala?.clientNom || "-"],
        ["Transporteur", dala?.transporteurNom || "-"],
        ["Commercial", dala?.commercialNom || "-"],
      ],
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42] },
      margin: tableMargin,
      theme: "grid",
    });

    const inputRows = filteredRows.map((r, i) => ([
      i + 1,
      Number(r.longeur || 0).toFixed(2),
      Number(r.largeur || 0).toFixed(2),
      r.depart ? "Oui" : "Non",
    ]));

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [["No", "Longueur", "Largeur", "Depart"]],
      body: inputRows.length ? inputRows : [["-", "-", "-", "-"]],
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [250, 250, 250], textColor: [15, 23, 42] },
      margin: tableMargin,
      theme: "grid",
    });

    const detailsRows = calculatedRows.map((r, i) => ([
      i + 1,
      r.articleNom || "-",
      Number(r.nbP || 0).toFixed(2),
      r.largeurML ? Number(r.largeurML).toFixed(2) : "-",
      Number(r.prixUnitaire || 0).toFixed(2),
      Number(r.montantLigne || 0).toFixed(2),
    ]));

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [["No", "Article", "Quantite", "ML", "PU", "Montant Ligne"]],
      body: detailsRows.length ? detailsRows : [["-", "Aucune ligne", "-", "-", "-", "-"]],
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42] },
      margin: tableMargin,
      theme: "grid",
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [["Resume", "Valeur"]],
      body: [
        ["Total Surface (m2)", Number(totalSurfaceCalc).toFixed(2)],
        ["Total ML", Number(totalMLCalc).toFixed(2)],
        ["Nombre PTS", Number(nombrePTSCalc || 0).toFixed(0)],
        ["Nombre Hourdis", Number(totalHourdisCalc).toFixed(0)],
        ["Nombre Poutrelle", Number(totalPoutrelleCalc).toFixed(2)],
        ["Prix m2", Number(prixMetreCarreCalc).toFixed(2)],
        ["Total", Number(totalMontantCalc).toFixed(2)],
      ],
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [163, 240, 183], textColor: [15, 23, 42] },
      margin: tableMargin,
      theme: "grid",
    });

    const line1 = [
      company?.address || null,
      [company?.phone1, company?.phone2].filter(Boolean).join(" / ") || null,
      company?.email || null,
    ].filter(Boolean).join(" | ");
    const line2 = [
      company?.identificationFiscale ? `IF: ${company.identificationFiscale}` : null,
      company?.ice ? `ICE: ${company.ice}` : null,
      company?.rc ? `RC: ${company.rc}` : null,
      company?.patente ? `Patente: ${company.patente}` : null,
      company?.cnss ? `CNSS: ${company.cnss}` : null,
    ].filter(Boolean).join(" | ");

    const pageCount = doc.getNumberOfPages();
    const pageHeight = doc.internal.pageSize.getHeight();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setDrawColor(180);
      doc.line(margin, pageHeight - 16, pageWidth - margin, pageHeight - 16);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(80);
      if (line1) doc.text(line1, pageWidth / 2, pageHeight - 10, { align: "center" });
      if (line2) doc.text(line2, pageWidth / 2, pageHeight - 6, { align: "center" });
      doc.text(`Page ${page} / ${pageCount}`, pageWidth - margin, pageHeight - 3, { align: "right" });
    }

    const pdfBlobUrl = doc.output("bloburl");
    window.open(pdfBlobUrl, "_blank");
  };

  const handlePreviewPdf = (id) => {
    dispatch(dalaActions.getById({
      id,
      onSuccess: (fullDala) => previewPdf(fullDala),
      onError: () => {
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: "Impossible de charger les details du rapport.",
        });
      }
    }));
  };

  return (
    <Container fluid className="dala-list-page">

      <Card className="shadow-sm border-0 mb-3 dala-list-header">
        <CardBody className="py-3">
          <Row className="align-items-center">
            <Col md={6}>
              <h4 className="mb-0 d-flex align-items-center text-primary">
                <FaLayerGroup className="me-2" />
                Liste des Calculs de Plancher
              </h4>
              <small className="text-muted">
                Suivi des projets, clients, transporteurs et montants.
              </small>
            </Col>

            <Col md={6} className="text-md-end mt-2 mt-md-0">
              <Button
                color="primary"
                className="me-2 shadow-sm"
                onClick={() => navigate("/dala-module/new")}
              >
                <FaPlus className="me-1" /> Nouveau Calcul
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Row className="mb-3 g-2">
        <Col md={4}>
          <Card className="shadow-sm border-0">
            <CardBody>
              <div className="text-muted small">Nombre de Projets</div>
              <h5 className="mb-0">{filteredDalas.length || 0}</h5>
            </CardBody>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm border-0">
            <CardBody>
              <div className="text-muted small">Surface Totale / Lignes</div>
              <h5 className="mb-0">{totalSurface.toFixed(2)} m2 <span className="text-muted fs-6">({totalLignes} lignes)</span></h5>
            </CardBody>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm border-0">
            <CardBody>
              <div className="text-muted small">Montant Total / Moyen</div>
              <h5 className="mb-0">{totalMontant.toFixed(2)} DH <span className="text-muted fs-6">({avgMontant.toFixed(2)})</span></h5>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm border-0">
        <CardBody>
          <Row className="mb-3">
            <Col md={6} lg={5}>
              <div className="d-flex align-items-center dala-search-box">
                <FaSearch className="me-2 text-muted" />
                <Input
                  placeholder="Rechercher ID, client, transporteur, commercial..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {!!search && (
                  <Button
                    type="button"
                    size="sm"
                    color="link"
                    className="text-secondary p-0 ms-2"
                    onClick={() => setSearch("")}
                  >
                    <FaTimesCircle />
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (filteredDalas?.length === 0 ? (
            <div className="text-center py-5 text-muted">
              Aucun calcul trouve.
            </div>
          ) : (

            <div className="dala-table-wrap">
              <Table hover responsive className="align-middle mb-0">
                <thead className="table-light position-sticky top-0">
                  <tr>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Total ML</th>
                    <th>Nombre Hourdis</th>
                    <th>Nombre Poutrelle</th>
                    <th>Surface</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {filteredDalas.map(d => (
                    <tr key={d.id}>
                      <td>
                        <Badge color="light" className="border text-dark">#{d.id}</Badge>
                      </td>

                      <td>{d.date || "-"}</td>

                      <td>
                        <b>{d.clientNom || "-"}</b>
                        <div className="text-muted small">{d.createdBy || "-"}</div>
                      </td>

                      <td>
                        <Badge color="light" className="border text-dark">
                          {Number(d.totalML || 0).toFixed(2)}
                        </Badge>
                      </td>

                      <td>
                        <Badge color="light" className="border text-dark">
                          {Number(d.nombreHourdis || 0).toFixed(0)}
                        </Badge>
                      </td>

                      <td>
                        <Badge color="light" className="border text-dark">
                          {Number(d.nombrePourtelle ?? d.nombrePoutrelle ?? 0).toFixed(0)}
                        </Badge>
                      </td>

                      <td>
                        <Badge color="info">
                          {Number(d.totalSurface || 0).toFixed(2)} m2
                        </Badge>
                      </td>

                      <td>
                        <Badge color="success">
                          {Number(d.total || 0).toFixed(2)} MAD
                        </Badge>
                      </td>

                      <td className="text-end">
                        <Button
                          size="sm"
                          color="danger"
                          className="me-2"
                          onClick={() => handlePreviewPdf(d.id)}
                        >
                          <FaFilePdf className="me-1" /> PDF
                        </Button>
                        <Button
                          size="sm"
                          color="warning"
                          onClick={() => navigate(`/dala-module/edit/${d.id}`)}
                        >
                          <FaEdit className="me-1" /> Modifier
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

          ))}

        </CardBody>
      </Card>
    </Container>
  );
}
